export const POD_OPTIMIZATION_MODES = ['gain', 'loss'] as const;
export type PodOptimizationMode = (typeof POD_OPTIMIZATION_MODES)[number];

export const SALES_CHANNELS = ['On-Premise', 'Small Format', 'Package Liquor', 'Large Format'] as const;
export type SalesChannel = (typeof SALES_CHANNELS)[number];

export const DISTRIBUTION_TYPES = ['SD', 'AXD', 'Package SD', 'Draught SD'] as const;
export type DistributionType = (typeof DISTRIBUTION_TYPES)[number];

export const GEO_LEVELS = ['national', 'region', 'state', 'rootgeo'] as const;
export type GeoLevel = (typeof GEO_LEVELS)[number];

export type PodConstraint = {
  id: string;
  enabled: boolean;
  brands: string[];
  salesChannels: SalesChannel[];
  distributionTypes: DistributionType[];
  geoLevel: GeoLevel;
  geos: string[];
  minPodChange?: number;
  maxPodChange?: number;
  parentMinPodChange?: number;
  parentMaxPodChange?: number;
  childMinPodChange?: number;
  childMaxPodChange?: number;
};

export type PodConstraintGroup = {
  id: string;
  constraints: PodConstraint[];
};

export type PodOptimizationRequest = {
  brands: string[];
  salesChannels: SalesChannel[];
  distributionTypes: DistributionType[];
  geoLevel: GeoLevel;
  geos: string[];
  dateRange: [string, string];
  mode: PodOptimizationMode;
  targetPods: number;
  constraints: PodConstraint[];
};

export type PodOptimizationApiScope = {
  channel_name?: string;
  brand_names: string[];
  geo_spread: GeoLevel;
  geo_names: string[];
  min_pod_change: number | null;
  max_pod_change: number | null;
};

export type PodOptimizationApiDistributionGroup = {
  distribution_type: string;
  min_pod_change: number | null;
  max_pod_change: number | null;
  optimize_over: PodOptimizationApiScope[];
};

export type PodDistributionAvailability = {
  distribution_type: string;
  channels?: Array<{ channel_name: string }>;
};

export type PodDistributionAvailabilitiesResponse = {
  distribution_types: PodDistributionAvailability[];
};

export type PodOptimizationApiRequest = {
  optimize_over: PodOptimizationApiDistributionGroup[];
  start_date: string;
  end_date: string;
  optimization_mode: PodOptimizationMode;
  pod_change: number;
};

export type PodOptimizationApiResultBase = {
  channel_name?: string | null;
  distribution_type: string;
  brand_name: string;
  geo_spread: GeoLevel;
  geo_name: string;
  baseline_pods: number;
  recommended_pod_change: number;
  optimized_pods: number;
  incremental_volume: number;
  incremental_volume_per_changed_pod: number;
  max_pod_change: number;
  last_pod_impact: number;
};

export type PodOptimizationApiPeriodResult = Omit<
  PodOptimizationApiResultBase,
  'channel_name' | 'distribution_type' | 'brand_name' | 'geo_spread' | 'geo_name'
> & { time_period: string };

export type PodOptimizationApiResultPod = PodOptimizationApiResultBase & {
  time_periods?: PodOptimizationApiPeriodResult[];
};

type PodOptimizationApiResponseScope = Omit<PodOptimizationApiScope, 'min_pod_change' | 'max_pod_change'> &
  Partial<Pick<PodOptimizationApiScope, 'min_pod_change' | 'max_pod_change'>>;

type PodOptimizationApiResponseDistributionGroup = Omit<
  PodOptimizationApiDistributionGroup,
  'min_pod_change' | 'max_pod_change' | 'optimize_over'
> &
  Partial<Pick<PodOptimizationApiDistributionGroup, 'min_pod_change' | 'max_pod_change'>> & {
    optimize_over: PodOptimizationApiResponseScope[];
  };

type PodOptimizationApiResponseRequest = Omit<PodOptimizationApiRequest, 'optimize_over'> & {
  optimize_over: PodOptimizationApiResponseDistributionGroup[];
};

export type PodOptimizationApiResponse = {
  request: PodOptimizationApiResponseRequest;
  result: {
    total_incremental_volume: number;
    total_pod_change: number;
    incremental_volume_per_changed_pod: number;
    last_pod_impact: number;
    pods: PodOptimizationApiResultPod[];
  };
};

export type PodOpportunity = {
  id: string;
  brand: string;
  salesChannel: SalesChannel;
  distributionType: DistributionType;
  national: string;
  region: string;
  state: string;
  rootgeo: string;
  baselinePods: number;
  maxPossiblePods: number;
  incrementalPodImpact: number;
  lastPodImpact: number;
};

export type PodOptimizationResultRow = {
  id: string;
  brand: string;
  salesChannel: SalesChannel;
  distributionType: DistributionType;
  week: string;
  geoLevel: GeoLevel;
  geo: string;
  national: string;
  region: string;
  state: string;
  rootgeo: string;
  baselinePods: number;
  recommendedPodChange: number;
  optimizedPods: number;
  expectedVolumeImpact: number;
  totalIncrementalVolume: number;
  totalPodChange: number;
  optimizationMode: PodOptimizationMode;
  filterValuesUsed: string;
  incrementalPodImpact: number;
  lastPodImpact: number;
  maxPossiblePods: number;
  incrementalVolume: number;
  incrementalVolumePerChangedPod: number;
  maxPodChange: number;
  timePeriod?: string;
  children?: PodOptimizationResultRow[];
};

export type PodOptimizationResult = {
  rows: PodOptimizationResultRow[];
  mode: PodOptimizationMode;
  weeklyTargetPods: number;
  weekCount: number;
  totalPodChange: number;
  totalIncrementalVolume: number;
  filterValuesUsed: string;
  incrementalVolumePerChangedPod: number;
  lastPodImpact: number;
};
